import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from './useRTK'
import { setVideoOn, setSize, selectVideoOn, selectSize } from '../app/canvas'
import { countCharOccurrences } from 'src/utils/helpers'
import { OnMyMediaChangeEvent, VirtualForegroundOptions } from 'src/types/sdk'
import { ParticipantBadge } from 'src/app/types'
import { addErrorSDKToast, context, canvas } from 'src/utils/helpers'
import {
  CANVAS_FONT_FAMILY,
  CANVAS_PADDING,
  CANVAS_FONT_SIZE,
  BADGE_SVG_URL_MAP,
} from 'src/constant'
import { zoomSdkApi } from 'src/app/zoomSdkQueries'

type BadgeWithXCoords = ParticipantBadge & {
  xCoordCount: number
  xCoordBadge: number
}

// This hook sets a users virtual background if there is:
// changes from prev myBadges
// the users video is on
// they are allowed to have their VB set (host settings allow)
export function useVirtualForeground(
  showBadges: boolean,
  myBadges: ParticipantBadge[]
) {
  const dispatch = useAppDispatch()
  const videoOn = useAppSelector(selectVideoOn)
  const { width, height } = useAppSelector(selectSize)
  const [vfOnVideo, setVfOnVideo] = useState(false)
  const { hasVFPersistence } = zoomSdkApi.endpoints.config.useQueryState(
    'config',
    {
      selectFromResult: (result) => ({
        hasVFPersistence: result.data?.hasVFPersistence,
      }),
    }
  )

  // changes to video
  useEffect(() => {
    const fn = ({ media }: OnMyMediaChangeEvent) => {
      const { video } = media
      if (video?.width && video?.height && typeof video?.state === 'boolean') {
        dispatch(setVideoOn(video.state))
        dispatch(setSize({ height: video.height, width: video.width }))
      }
    }
    try {
      window.zoomSdk.onMyMediaChange(fn)
    } catch {
      addErrorSDKToast('Error: cannot add onMyMediaChange event')
    }

    return () => window.zoomSdk.removeEventListener('onMyMediaChange', fn)
  }, [dispatch])

  // set / remove VF
  useEffect(() => {
    if (showBadges && videoOn && canvas && context) {
      canvas.width = Math.floor(width - CANVAS_PADDING)
      canvas.height = Math.floor(height - CANVAS_PADDING)

      // The size of the count is set with the font
      context.font = `300 ${CANVAS_FONT_SIZE}px ${CANVAS_FONT_FAMILY}`
      context.fillStyle = 'white'
      context.textAlign = 'right'
      context.textBaseline = 'bottom'

      // guarantee order even on 1st load/ before addBadge is invoked
      const myBadgesOrdered: ParticipantBadge[] = myBadges
        .slice()
        .sort((a, b) => a.count - b.count)
      const yOffsetBadge = 54 // offset from side of screen
      const yCoordBadge = canvas.height - yOffsetBadge // same for all badges
      const yCoordCount = canvas.height // same for all counts - uses padding
      let xOffset = 0 // incremented depending on length of nums

      // linearly add xCoords for badge and count
      const myBadgesWithXCoords: BadgeWithXCoords[] = myBadgesOrdered.map(
        (badge) => {
          const { count } = badge
          const countString = String(count)
          const howManyOnes = countCharOccurrences(countString, '1')
          const howManyOtherInts = countString.length - howManyOnes
          const xOffsetCount = xOffset
          const xCoordCount = canvas.width - xOffsetCount

          xOffset += 50 + howManyOtherInts * 30 + howManyOnes * 26 // handle ints > 10 or even 1000 etc dynamically
          const xOffsetBadge = xOffset // Each image is same px from its neighbor's left-most x coord
          const xCoordBadge = canvas.width - xOffsetBadge
          xOffset += 26 // space between each badge and next one's count

          return { ...badge, xCoordCount, xCoordBadge }
        }
      )

      // async load the images and add to context
      const loadImagesPromises = myBadgesWithXCoords.map((badge) => {
        return new Promise<void>((resolve) => {
          const { name, count, xCoordCount, xCoordBadge } = badge
          const svgString = BADGE_SVG_URL_MAP[name]
          const imageEl = document.createElement('img')
          const needsVerticalNudgeDown = [
            'medal',
            'check-mark-button',
          ].includes(badge.name)
          const verticalNudgePx = needsVerticalNudgeDown ? 2 : 0
          imageEl.src = svgString

          // per constant.ts, this onload event appears to be not true (not loaded) IF images is not in browser mem cache
          imageEl.onload = () => {
            context.drawImage(
              imageEl, // the badge svg rendered in an img element
              xCoordBadge,
              yCoordBadge + verticalNudgePx
            )

            context.fillText(String(count), xCoordCount, yCoordCount)

            resolve()
          }
        })
      })

      Promise.all(loadImagesPromises).then(() => {
        try {
          const imageData = context.getImageData(
            0,
            0,
            canvas.width,
            canvas.height
          )

          const settings: VirtualForegroundOptions = {
            imageData,
          }
          if (hasVFPersistence) {
            settings.persistence = 'app'
          }

          window.zoomSdk.setVirtualForeground(settings)
          setVfOnVideo(true)
        } catch (error) {
          addErrorSDKToast(error)
        }
      })
    } else {
      // sdk removal requires their video to be on. Not catching the error since removal when no vf is present throws an annoying error
      if (videoOn && vfOnVideo) {
        window.zoomSdk.callZoomApi('removeVirtualForeground')
        setVfOnVideo(false)
      }
    }
  }, [
    videoOn,
    showBadges,
    myBadges,
    height,
    width,
    dispatch,
    vfOnVideo,
    hasVFPersistence,
  ])
}
